import React, { ComponentProps, createContext, PropsWithChildren } from "react";
import Animated, {
  Easing,
  ReduceMotion,
  SharedValue,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { View, ViewProps } from "tamagui";

import { Button } from "../../ui";

interface ChatUiSideBarContext {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  offsetX: SharedValue<number>;
  width: number;
  //  side: "left" | "right";
}

const ChatUiSideBarContext = createContext<ChatUiSideBarContext | null>(null);

const useChatUiSideBarContext = () => {
  const context = React.useContext(ChatUiSideBarContext);
  if (!context) {
    throw new Error(
      "useChatUiSideBarContext must be used within a ChatUiSideBarProvider",
    );
  }
  const toggle = () => context.setOpen((prev) => !prev);

  return { toggle, ...context };
};

const SidebarProvider: React.FC<
  PropsWithChildren & {
    width?: number;
    //NOTE: right side bar not implemented yet
    //    side?: "left" | "right";
  }
> = ({
  children,
  // side = "left",
  width = 300,
}) => {
  const [open, setOpen] = React.useState(true);
  const offsetX = useSharedValue(0);
  return (
    <ChatUiSideBarContext.Provider
      value={{
        open,
        setOpen,
        offsetX,
        width,
        //    side,
      }}
    >
      {children}
    </ChatUiSideBarContext.Provider>
  );
};

type SidebarContainerProps = ComponentProps<typeof Animated.View>;

const SidebarContent: React.FC<SidebarContainerProps> = ({
  children,
  style,
  ...rest
}) => {
  const { offsetX, width } = useChatUiSideBarContext();
  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateX: (width - offsetX.value) * -1 }],
      width: offsetX.value,
    };
  });

  return (
    <Animated.View style={[style, animatedStyle]} {...rest}>
      {children}
    </Animated.View>
  );
};

const SidebarContainer: React.FC<ViewProps> = ({ children, ...rest }) => {
  return (
    <View overflow="hidden" {...rest}>
      {children}
    </View>
  );
};

type ButtonProps = Exclude<ComponentProps<typeof Button>, "children">;

function ToggleExpandButton(props: {
  asChild: true;
  children: React.ReactElement | ((val: boolean) => React.ReactElement);
}): React.JSX.Element;

function ToggleExpandButton(
  props: {
    asChild?: false;
    children: React.ReactNode;
  } & ButtonProps,
): React.JSX.Element;

function ToggleExpandButton({
  asChild = false,
  children,
  ...rest
}:
  | {
      asChild: true;
      children: React.ReactElement | ((val: boolean) => React.ReactElement);
    }
  | ({ asChild?: false; children?: React.ReactNode } & ButtonProps)) {
  const { open, toggle, offsetX, width } = useChatUiSideBarContext();

  const onPress = () => toggle();

  React.useEffect(() => {
    offsetX.value = withTiming(open ? width : 0, {
      duration: 300,
      easing: Easing.inOut(Easing.ease),
      reduceMotion: ReduceMotion.Never,
    });
  }, [offsetX, open, width]);

  if (asChild) {
    const config = {
      onPress,
    };

    return typeof children === "function"
      ? React.cloneElement(children(open), config)
      : React.cloneElement(children, config);
  }

  return (
    <Button onPress={onPress} {...rest}>
      {children}
    </Button>
  );
}

export {
  SidebarContainer,
  SidebarContent,
  SidebarProvider,
  ToggleExpandButton,
};
